import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getDateTimeFormatMapForLocale, { defaultDateTimeFormatMap } from '@rover/utilities/datetime';

import { useRevampCta } from '../../hooks/useRevampCta';
import { useUpdateSelectedDates } from '../../hooks/useUpdateSelectedDates';
import { expandRangeToDates, LOOSE_DATES_PARAM, parseLooseDates } from '../../utils/looseDates';
import { FREQUENCY_PARAM, isRecurringFrequency, parseWeekDays } from '../../utils/recurringDates';

import { SitterProfileDatesChanged } from './analyticsEvents';
import { DateSelection } from './useRevampDatesSelection';

export type DateCapabilities = {
  allowsLooseDates: boolean;
  allowsRecurring: boolean;
};

const readIsoDate = (params: URLSearchParams, name: string, locale: Locale): string | undefined => {
  const value = params.get(name);
  if (!value) return undefined;
  const DF = getDateTimeFormatMapForLocale(locale);
  const date = moment(value, [DF.DATE_ISO, DF.DATE_SHORT]);
  return date.isValid() ? date.format(DF.DATE_ISO) : value;
};

const toDate = (isoDate?: string): Date | undefined =>
  isoDate ? moment(isoDate).toDate() : undefined;

export const describeDateSelection = (
  params: URLSearchParams,
  locale: Locale,
  { allowsLooseDates, allowsRecurring }: DateCapabilities
): string => {
  const startDate = readIsoDate(params, 'start_date', locale);

  if (allowsRecurring && isRecurringFrequency(params.get(FREQUENCY_PARAM))) {
    return `repeat:${startDate ?? ''}:${parseWeekDays(params).join(',')}`;
  }

  const looseDates = allowsLooseDates ? parseLooseDates(params.get(LOOSE_DATES_PARAM)) : [];
  const days =
    looseDates.length > 0
      ? looseDates
      : expandRangeToDates(toDate(startDate), toDate(readIsoDate(params, 'end_date', locale)));
  return `days:${days
    .map((day) => moment(day).format(defaultDateTimeFormatMap.DATE_ISO))
    .join(',')}`;
};

const useSaveRevampDates = (locale: Locale): ((selection: DateSelection) => void) => {
  const location = useLocation();
  const { sitterData, serviceType, selectedService } = useRevampCta();
  const updateSelectedDates = useUpdateSelectedDates();

  const allowsLooseDates = selectedService?.serviceType.supportsNonContiguousDates === true;
  const allowsRecurring = selectedService?.serviceType.supportsRecurringBilling === true;

  return useCallback(
    (selection: DateSelection) => {
      const before = new URLSearchParams(location.search);
      const after = updateSelectedDates(selection);

      const startDate = readIsoDate(after, 'start_date', locale);
      if (!startDate) return;

      const capabilities = { allowsLooseDates, allowsRecurring };
      if (
        describeDateSelection(before, locale, capabilities) ===
        describeDateSelection(after, locale, capabilities)
      ) {
        return;
      }

      emitAnalyticsEvent(
        new SitterProfileDatesChanged({
          providerId: sitterData.opk,
          serviceSlug: serviceType ?? '',
          startDate,
          endDate: readIsoDate(after, 'end_date', locale),
          hadDates: !!before.get('start_date'),
          pageVariant: 'revamp',
        })
      );
    },
    [
      location.search,
      updateSelectedDates,
      locale,
      allowsLooseDates,
      allowsRecurring,
      sitterData.opk,
      serviceType,
    ]
  );
};

export default useSaveRevampDates;
